import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Building2, Plus, Loader2, User, Phone, Mail, Briefcase, ChevronLeft,
  FileText, Calendar, ClipboardList, Package, Receipt, FileSignature, Eye, Download,
  FileBarChart, Search, X, Users, ChevronRight, Sun, CloudSun
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const WorkSubcontractors = () => {
  const { user } = useAuth();
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [detailView, setDetailView] = useState(false);
  const [activeTab, setActiveTab] = useState('hakedis');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Badge/Yeni sayaç sistemi
  const [newCounts, setNewCounts] = useState({ subcontractors: {}, total_new: 0 });
  
  // Tab data states
  const [hakedisData, setHakedisData] = useState([]);
  const [sozlesmeData, setSozlesmeData] = useState([]);
  const [isProgramiData, setIsProgramiData] = useState([]);
  const [tutanakData, setTutanakData] = useState([]);
  const [isBilgisiData, setIsBilgisiData] = useState([]);
  const [malzemeData, setMalzemeData] = useState([]);
  const [raporlarData, setRaporlarData] = useState([]);
  const [personelData, setPersonelData] = useState({ employees: [], total_count: 0, sabah_count: 0, ogle_count: 0, bildirim_yok_count: 0, date: '' });
  const [personelDate, setPersonelDate] = useState(new Date().toISOString().split('T')[0]);
  const [tabLoading, setTabLoading] = useState(false);
  
  // Modal states for adding
  const [hakedisModalOpen, setHakedisModalOpen] = useState(false);
  const [sozlesmeModalOpen, setSozlesmeModalOpen] = useState(false);
  const [isProgramiModalOpen, setIsProgramiModalOpen] = useState(false);
  const [tutanakModalOpen, setTutanakModalOpen] = useState(false);
  const [isBilgisiModalOpen, setIsBilgisiModalOpen] = useState(false);
  const [raporModalOpen, setRaporModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [hakedisForm, setHakedisForm] = useState({
    hakedis_date: new Date().toISOString().split('T')[0],
    title: '', amount: '', description: '', documents: []
  });
  const [sozlesmeForm, setSozlesmeForm] = useState({
    contract_date: new Date().toISOString().split('T')[0],
    work_type: '', start_date: '', end_date: '', documents: []
  });
  const [isProgramiForm, setIsProgramiForm] = useState({
    subject: '', description: '', start_date: '', end_date: '', documents: []
  });
  const [tutanakForm, setTutanakForm] = useState({
    report_date: new Date().toISOString().split('T')[0],
    title: '', content: '', description: '', documents: []
  });
  const [isBilgisiForm, setIsBilgisiForm] = useState({
    report_date: new Date().toISOString().split('T')[0],
    title: '', content: '', description: '', documents: []
  });
  const [raporForm, setRaporForm] = useState({
    report_date: new Date().toISOString().split('T')[0],
    report_type: 'daily',
    title: '', content: '', description: '', documents: []
  });

  // Load subcontractors
  const loadSubcontractors = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/work/subcontractors`, { headers: getAuthHeaders() });
      setSubcontractors(res.data || []);
    } catch (error) {
      console.error('Taşeronlar yüklenemedi:', error);
      toast.error('Taşeronlar yüklenemedi');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSubcontractors();
    loadNewCounts();
  }, [loadSubcontractors]);

  // Yeni kayıt sayılarını yükle
  const loadNewCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/work/subcontractors/new-counts`, { headers: getAuthHeaders() });
      setNewCounts(res.data || { subcontractors: {}, total_new: 0 });
    } catch (error) {
      console.error('Yeni sayılar yüklenemedi:', error);
    }
  }, []);

  // Sekme görüldü olarak işaretle
  const markTabSeen = useCallback(async (subcontractorId, tab) => {
    try {
      await axios.post(`${API}/work/subcontractors/mark-seen`, 
        { subcontractor_id: subcontractorId, tab: tab },
        { headers: getAuthHeaders() }
      );
      // Sayaçları güncelle
      setTimeout(() => loadNewCounts(), 500);
    } catch (error) {
      console.error('Mark seen hatası:', error);
    }
  }, [loadNewCounts]);

  // Taşeronun yeni kayıt sayısını al
  const getSubcontractorNewCount = (subId) => {
    return newCounts.subcontractors?.[subId]?.total || 0;
  };

  // Sekmenin yeni kayıt sayısını al
  const getTabNewCount = (subId, tab) => {
    return newCounts.subcontractors?.[subId]?.tabs?.[tab] || 0;
  };

  // Load tab data
  const loadTabData = useCallback(async (tab) => {
    if (!selectedSubcontractor) return;
    setTabLoading(true);
    try {
      const endpoints = {
        'hakedis': `/work/subcontractors/${selectedSubcontractor.id}/hakedis`,
        'sozlesme': `/work/subcontractors/${selectedSubcontractor.id}/sozlesme`,
        'is-programi': `/work/subcontractors/${selectedSubcontractor.id}/is-programi`,
        'tutanaklar': `/work/subcontractors/${selectedSubcontractor.id}/tutanaklar`,
        'is-bilgisi': `/work/subcontractors/${selectedSubcontractor.id}/is-bilgisi`,
        'malzeme': `/work/subcontractors/${selectedSubcontractor.id}/malzeme-kullanimi`,
        'raporlar': `/work/subcontractors/${selectedSubcontractor.id}/reports`,
        'personel': `/work/subcontractors/${selectedSubcontractor.id}/personel?date=${personelDate}`
      };
      
      if (endpoints[tab]) {
        const res = await axios.get(`${API}${endpoints[tab]}`, { headers: getAuthHeaders() });
        switch(tab) {
          case 'hakedis': setHakedisData(res.data || []); break;
          case 'sozlesme': setSozlesmeData(res.data || []); break;
          case 'is-programi': setIsProgramiData(res.data || []); break;
          case 'tutanaklar': setTutanakData(res.data || []); break;
          case 'is-bilgisi': setIsBilgisiData(res.data || []); break;
          case 'malzeme': setMalzemeData(res.data || []); break;
          case 'raporlar': setRaporlarData(res.data || []); break;
          case 'personel': setPersonelData(res.data || { employees: [], total_count: 0 }); break;
          default: break;
        }
      }
    } catch (error) {
      console.error(`${tab} verileri yüklenirken hata:`, error);
    }
    setTabLoading(false);
  }, [selectedSubcontractor, personelDate]);

  useEffect(() => {
    if (selectedSubcontractor && detailView) {
      loadTabData(activeTab);
      // Sekme değişince mark-seen çağır
      markTabSeen(selectedSubcontractor.id, activeTab);
    }
  }, [selectedSubcontractor, activeTab, detailView, loadTabData, markTabSeen]);

  // Personel tarihi değiştiğinde yeniden yükle
  const handlePersonelDateChange = (newDate) => {
    setPersonelDate(newDate);
    if (activeTab === 'personel') {
      loadTabData('personel');
    }
  };

  // File upload handler
  const handleFileUpload = (e, setFormFunc, formKey) => {
    const files = e.target.files;
    if (!files.length) return;
    
    const newDocs = [];
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        newDocs.push({
          name: file.name, type: file.type, size: file.size,
          data: reader.result, uploaded_at: new Date().toISOString()
        });
        if (newDocs.length === files.length) {
          setFormFunc(prev => ({
            ...prev,
            [formKey]: [...(prev[formKey] || []), ...newDocs]
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeDocument = (setFormFunc, formKey, index) => {
    setFormFunc(prev => ({
      ...prev,
      [formKey]: prev[formKey].filter((_, i) => i !== index)
    }));
  };

  // CREATE Operations (Çalışanlar ekleme yapabilir)
  const handleCreateHakedis = async () => {
    if (!hakedisForm.title.trim()) { toast.error('Başlık gerekli'); return; }
    try {
      await axios.post(`${API}/work/subcontractors/${selectedSubcontractor.id}/hakedis`, hakedisForm, { headers: getAuthHeaders() });
      toast.success('Hakediş eklendi');
      setHakedisModalOpen(false);
      setHakedisForm({ hakedis_date: new Date().toISOString().split('T')[0], title: '', amount: '', description: '', documents: [] });
      loadTabData('hakedis');
    } catch (error) { toast.error('Hakediş eklenemedi'); }
  };

  const handleCreateSozlesme = async () => {
    if (!sozlesmeForm.work_type.trim()) { toast.error('İş türü gerekli'); return; }
    try {
      await axios.post(`${API}/work/subcontractors/${selectedSubcontractor.id}/sozlesme`, sozlesmeForm, { headers: getAuthHeaders() });
      toast.success('Sözleşme eklendi');
      setSozlesmeModalOpen(false);
      setSozlesmeForm({ contract_date: new Date().toISOString().split('T')[0], work_type: '', start_date: '', end_date: '', documents: [] });
      loadTabData('sozlesme');
    } catch (error) { toast.error('Sözleşme eklenemedi'); }
  };

  const handleCreateIsProgrami = async () => {
    if (!isProgramiForm.subject.trim()) { toast.error('Konu gerekli'); return; }
    try {
      await axios.post(`${API}/work/subcontractors/${selectedSubcontractor.id}/is-programi`, isProgramiForm, { headers: getAuthHeaders() });
      toast.success('İş programı eklendi');
      setIsProgramiModalOpen(false);
      setIsProgramiForm({ subject: '', description: '', start_date: '', end_date: '', documents: [] });
      loadTabData('is-programi');
    } catch (error) { toast.error('İş programı eklenemedi'); }
  };

  const handleCreateTutanak = async () => {
    if (!tutanakForm.title.trim()) { toast.error('Başlık gerekli'); return; }
    try {
      await axios.post(`${API}/work/subcontractors/${selectedSubcontractor.id}/tutanaklar`, tutanakForm, { headers: getAuthHeaders() });
      toast.success('Tutanak eklendi');
      setTutanakModalOpen(false);
      setTutanakForm({ report_date: new Date().toISOString().split('T')[0], title: '', content: '', description: '', documents: [] });
      loadTabData('tutanaklar');
    } catch (error) { toast.error('Tutanak eklenemedi'); }
  };

  const handleCreateIsBilgisi = async () => {
    if (!isBilgisiForm.title.trim()) { toast.error('Başlık gerekli'); return; }
    try {
      await axios.post(`${API}/work/subcontractors/${selectedSubcontractor.id}/is-bilgisi`, isBilgisiForm, { headers: getAuthHeaders() });
      toast.success('İş bilgisi eklendi');
      setIsBilgisiModalOpen(false);
      setIsBilgisiForm({ report_date: new Date().toISOString().split('T')[0], title: '', content: '', description: '', documents: [] });
      loadTabData('is-bilgisi');
    } catch (error) { toast.error('İş bilgisi eklenemedi'); }
  };

  const handleCreateRapor = async () => {
    if (!raporForm.title.trim()) { toast.error('Başlık gerekli'); return; }
    try {
      await axios.post(`${API}/work/subcontractors/${selectedSubcontractor.id}/reports`, raporForm, { headers: getAuthHeaders() });
      toast.success('Rapor eklendi');
      setRaporModalOpen(false);
      setRaporForm({ report_date: new Date().toISOString().split('T')[0], report_type: 'daily', title: '', content: '', description: '', documents: [] });
      loadTabData('raporlar');
    } catch (error) { toast.error('Rapor eklenemedi'); }
  };

  const openItemDetail = (item) => {
    setSelectedItem(item);
    setDetailModalOpen(true);
  };

  const downloadDocument = (doc) => {
    if (doc.data) {
      const link = document.createElement('a');
      link.href = doc.data;
      link.download = doc.name;
      link.click();
    }
  };

  const openDetailView = (sub) => {
    setSelectedSubcontractor(sub);
    setDetailView(true);
    setActiveTab('hakedis');
    // Taşeron açıldığında tüm sekmeleri görüldü işaretle (mevcut içerik artık "yeni" değil).
    // Sonradan eklenen kayıtlar tekrar "yeni" olarak görünecektir.
    markTabSeen(sub.id, 'all');
  };

  const closeDetailView = () => {
    setDetailView(false);
    setSelectedSubcontractor(null);
  };

  const filteredSubcontractors = subcontractors.filter(sub => 
    (sub.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (sub.company_duties || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  // Detail View
  if (detailView && selectedSubcontractor) {
    return (
      <div className="space-y-6" data-testid="work-subcontractor-detail">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={closeDetailView} className="gap-2">
            <ChevronLeft className="h-4 w-4" />Geri
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{selectedSubcontractor.company_name}</h1>
            <p className="text-gray-500">{selectedSubcontractor.company_duties}</p>
          </div>
        </div>

        {/* Yetkili Bilgileri */}
        {selectedSubcontractor.contacts?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Yetkili Kişiler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedSubcontractor.contacts.map((contact, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-amber-600" />
                      <span className="font-medium">{contact.name || '-'}</span>
                    </div>
                    {contact.title && <div className="flex items-center gap-2 text-sm text-gray-600"><Briefcase className="h-3 w-3" />{contact.title}</div>}
                    {contact.phone && <div className="flex items-center gap-2 text-sm text-gray-600"><Phone className="h-3 w-3" />{contact.phone}</div>}
                    {contact.email && <div className="flex items-center gap-2 text-sm text-gray-600"><Mail className="h-3 w-3" />{contact.email}</div>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList className="inline-flex w-auto min-w-full">
              <TabsTrigger value="hakedis" className="gap-1 relative px-3">
                <Receipt className="h-4 w-4" /><span className="hidden md:inline">Hakediş</span>
                {getTabNewCount(selectedSubcontractor.id, 'hakedis') > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500 text-white">
                    {getTabNewCount(selectedSubcontractor.id, 'hakedis')}
                  </Badge>
                )}
              </TabsTrigger>
              {selectedSubcontractor.can_view_contracts !== false && (
                <TabsTrigger value="sozlesme" className="gap-1 relative px-3">
                  <FileSignature className="h-4 w-4" /><span className="hidden md:inline">Sözleşme</span>
                  {getTabNewCount(selectedSubcontractor.id, 'sozlesme') > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500 text-white">
                      {getTabNewCount(selectedSubcontractor.id, 'sozlesme')}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="personel" className="gap-1 relative px-3">
                <Users className="h-4 w-4" /><span className="hidden md:inline">Personel</span>
                {personelData.total_count > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-blue-500 text-white">
                    {personelData.total_count}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="is-programi" className="gap-1 relative px-3">
                <Calendar className="h-4 w-4" /><span className="hidden md:inline">İş Programı</span>
                {getTabNewCount(selectedSubcontractor.id, 'is-programi') > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500 text-white">
                    {getTabNewCount(selectedSubcontractor.id, 'is-programi')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="tutanaklar" className="gap-1 relative px-3">
                <ClipboardList className="h-4 w-4" /><span className="hidden md:inline">Tutanaklar</span>
                {getTabNewCount(selectedSubcontractor.id, 'tutanaklar') > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500 text-white">
                    {getTabNewCount(selectedSubcontractor.id, 'tutanaklar')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="is-bilgisi" className="gap-1 relative px-3">
                <FileText className="h-4 w-4" /><span className="hidden md:inline">İş Bilgisi</span>
                {getTabNewCount(selectedSubcontractor.id, 'is-bilgisi') > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500 text-white">
                    {getTabNewCount(selectedSubcontractor.id, 'is-bilgisi')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="malzeme" className="gap-1 relative px-3">
                <Package className="h-4 w-4" /><span className="hidden md:inline">Malzeme</span>
                {getTabNewCount(selectedSubcontractor.id, 'malzeme') > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500 text-white">
                    {getTabNewCount(selectedSubcontractor.id, 'malzeme')}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="raporlar" className="gap-1 relative px-3">
                <FileBarChart className="h-4 w-4" /><span className="hidden md:inline">Raporlar</span>
                {getTabNewCount(selectedSubcontractor.id, 'raporlar') > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px] bg-red-500 text-white">
                    {getTabNewCount(selectedSubcontractor.id, 'raporlar')}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Hakediş Tab */}
          <TabsContent value="hakedis" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-amber-600" />Hakediş</CardTitle>
                <Button onClick={() => setHakedisModalOpen(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
                  <Plus className="h-4 w-4" />HAKEDİŞ EKLE
                </Button>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
                ) : hakedisData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz hakediş kaydı yok</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Başlık</TableHead>
                        <TableHead>Miktar (TL)</TableHead>
                        <TableHead>Yapan</TableHead>
                        <TableHead>Belgeler</TableHead>
                        <TableHead className="text-center">Şube Onay</TableHead>
                        <TableHead className="text-center">Yönetim Onay</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {hakedisData.map(item => (
                        <TableRow key={item.id} className={item.is_new ? 'bg-red-50 ring-2 ring-red-400 ring-inset' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {item.hakedis_date}
                              {item.is_new && <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{item.title}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {item.amount ? `${Number(item.amount).toLocaleString('tr-TR')} TL` : '-'}
                          </TableCell>
                          <TableCell>{item.created_by_name || '-'}</TableCell>
                          <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                          <TableCell className="text-center">
                            {item.branch_manager_approved ? (
                              <Badge className="bg-green-100 text-green-700">✓ Onaylandı</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600">Yönetici onayı bekleniyor</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {item.management_approved ? (
                              <Badge className="bg-blue-100 text-blue-700">✓ Onaylandı</Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-400">Bekliyor</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sözleşme Tab - Sadece yetkili kullanıcılara göster */}
          {selectedSubcontractor.can_view_contracts !== false && (
            <TabsContent value="sozlesme" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-amber-600" />Sözleşme</CardTitle>
                </CardHeader>
                <CardContent>
                  {tabLoading ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
                  ) : sozlesmeData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500"><FileSignature className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz sözleşme yok</p></div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarih</TableHead>
                          <TableHead>İş Türü</TableHead>
                          <TableHead>Başlangıç</TableHead>
                          <TableHead>Bitiş</TableHead>
                          <TableHead>Belgeler</TableHead>
                          <TableHead>İşlemler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sozlesmeData.map(item => (
                          <TableRow key={item.id} className={item.is_new ? 'bg-red-50 ring-2 ring-red-400 ring-inset' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {item.contract_date}
                                {item.is_new && <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{item.work_type}</TableCell>
                            <TableCell>{item.start_date}</TableCell>
                            <TableCell>{item.end_date}</TableCell>
                            <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Personel Tab */}
          <TabsContent value="personel" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Personel Listesi
                  <Badge variant="outline" className="ml-2">{personelData.total_count} kişi</Badge>
                </CardTitle>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const prev = new Date(personelDate);
                        prev.setDate(prev.getDate() - 1);
                        handlePersonelDateChange(prev.toISOString().split('T')[0]);
                      }}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Input
                      type="date"
                      value={personelDate}
                      onChange={(e) => handlePersonelDateChange(e.target.value)}
                      className="w-40"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const next = new Date(personelDate);
                        next.setDate(next.getDate() + 1);
                        handlePersonelDateChange(next.toISOString().split('T')[0]);
                      }}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Badge className="bg-amber-100 text-amber-700">
                      <Sun className="h-3 w-3 mr-1" />
                      Sabah: {personelData.sabah_count || 0}
                    </Badge>
                    <Badge className="bg-orange-100 text-orange-700">
                      <CloudSun className="h-3 w-3 mr-1" />
                      Öğle: {personelData.ogle_count || 0}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
                ) : personelData.employees?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Bu taşerona ait personel kaydı bulunmuyor</p>
                    <p className="text-sm mt-1">Personeller İK Panelinden bu taşerona atanabilir</p>
                  </div>
                ) : (
                  <div className="max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ad Soyad</TableHead>
                          <TableHead>Pozisyon</TableHead>
                          <TableHead>Meslek</TableHead>
                          <TableHead>Telefon</TableHead>
                          <TableHead className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Sun className="h-4 w-4 text-amber-500" />
                              SABAH MESAİ
                            </div>
                          </TableHead>
                          <TableHead className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <CloudSun className="h-4 w-4 text-orange-500" />
                              ÖĞLE MESAİ
                            </div>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {personelData.employees?.map(emp => (
                          <TableRow key={emp.id}>
                            <TableCell className="font-medium">{emp.name}</TableCell>
                            <TableCell>{emp.position_name || emp.gorev || '-'}</TableCell>
                            <TableCell>{emp.profession || emp.meslek || '-'}</TableCell>
                            <TableCell>{emp.phone || '-'}</TableCell>
                            <TableCell className="text-center">
                              {emp.attendance?.morning_time ? (
                                <Badge className="bg-green-100 text-green-700 font-mono">
                                  {emp.attendance.morning_time}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-400">
                                  Bildirim Yok
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {emp.attendance?.afternoon_time ? (
                                <Badge className="bg-blue-100 text-blue-700 font-mono">
                                  {emp.attendance.afternoon_time}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-400">
                                  Bildirim Yok
                                </Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* İş Programı Tab */}
          <TabsContent value="is-programi" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-amber-600" />İş Programı</CardTitle>
                <Button onClick={() => setIsProgramiModalOpen(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
                  <Plus className="h-4 w-4" />İŞ PROGRAMI EKLE
                </Button>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
                ) : isProgramiData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz iş programı yok</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Konu</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Başlangıç</TableHead>
                        <TableHead>Bitiş</TableHead>
                        <TableHead>Belgeler</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isProgramiData.map(item => (
                        <TableRow key={item.id} className={item.is_new ? 'bg-red-50 ring-2 ring-red-400 ring-inset' : ''}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {item.subject}
                              {item.is_new && <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                          <TableCell>{item.start_date}</TableCell>
                          <TableCell>{item.end_date}</TableCell>
                          <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tutanaklar Tab */}
          <TabsContent value="tutanaklar" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-amber-600" />Tutanaklar</CardTitle>
                <Button onClick={() => setTutanakModalOpen(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
                  <Plus className="h-4 w-4" />TUTANAK EKLE
                </Button>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
                ) : tutanakData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz tutanak yok</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Başlık</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Belgeler</TableHead>
                        <TableHead className="text-center">Şube Onay</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tutanakData.map(item => (
                        <TableRow key={item.id} className={item.is_new ? 'bg-red-50 ring-2 ring-red-400 ring-inset' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {item.report_date || new Date(item.created_at).toLocaleDateString('tr-TR')}
                              {item.is_new && <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{item.title}</TableCell>
                          <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                          <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                          <TableCell className="text-center">
                            {item.branch_manager_approved ? (
                              <Badge className="bg-green-100 text-green-700">✓ Onaylandı</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600">Yönetici onayı bekleniyor</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* İş Bilgisi Tab */}
          <TabsContent value="is-bilgisi" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-amber-600" />İş Bilgisi</CardTitle>
                <Button onClick={() => setIsBilgisiModalOpen(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
                  <Plus className="h-4 w-4" />İŞ BİLGİSİ EKLE
                </Button>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
                ) : isBilgisiData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><FileText className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz iş bilgisi yok</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Başlık</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Belgeler</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isBilgisiData.map(item => (
                        <TableRow key={item.id} className={item.is_new ? 'bg-red-50 ring-2 ring-red-400 ring-inset' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {item.report_date || new Date(item.created_at).toLocaleDateString('tr-TR')}
                              {item.is_new && <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{item.title}</TableCell>
                          <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                          <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Malzeme Tab */}
          <TabsContent value="malzeme" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-amber-600" />Malzeme Kullanımı</CardTitle>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
                ) : malzemeData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><Package className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz malzeme kaydı yok</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Malzeme</TableHead>
                        <TableHead>Miktar</TableHead>
                        <TableHead>Birim</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Belgeler</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {malzemeData.map(item => (
                        <TableRow key={item.id} className={item.is_new ? 'bg-red-50 ring-2 ring-red-400 ring-inset' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {item.usage_date}
                              {item.is_new && <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{item.material_name}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell className="max-w-xs truncate">{item.description}</TableCell>
                          <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Raporlar Tab */}
          <TabsContent value="raporlar" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><FileBarChart className="h-5 w-5 text-amber-600" />Raporlar</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{raporlarData.length} rapor</Badge>
                  <Button onClick={() => setRaporModalOpen(true)} className="bg-amber-600 hover:bg-amber-700 gap-2">
                    <Plus className="h-4 w-4" />RAPOR EKLE
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>
                ) : raporlarData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><FileBarChart className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Bu taşerona taşınmış rapor yok</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rapor Türü</TableHead>
                        <TableHead>Başlık</TableHead>
                        <TableHead>Gönderen</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {raporlarData.map(report => (
                        <TableRow key={report.id} className={report.is_new ? 'bg-red-50 ring-2 ring-red-400 ring-inset' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">
                                {report.report_type === 'daily' ? 'Günlük' : report.report_type === 'weekly' ? 'Haftalık' : report.report_type === 'monthly' ? 'Aylık' : 'Rapor'}
                              </Badge>
                              {report.is_new && <Badge className="bg-red-500 text-white animate-pulse text-xs">YENİ</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{report.title || 'Başlıksız'}</TableCell>
                          <TableCell>{report.from_user?.name || report.created_by_name || '-'}</TableCell>
                          <TableCell>{report.created_at ? new Date(report.created_at).toLocaleDateString('tr-TR') : '-'}</TableCell>
                          <TableCell>
                            {report.status === 'completed' ? (
                              <Badge className="bg-green-100 text-green-700">Tamamlandı</Badge>
                            ) : (
                              <Badge variant="outline">{report.status || 'Bekliyor'}</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="outline" onClick={() => openItemDetail(report)}><Eye className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Modals */}
        {/* Hakediş Modal */}
        <Dialog open={hakedisModalOpen} onOpenChange={setHakedisModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-amber-600" />Hakediş Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input type="date" value={hakedisForm.hakedis_date} onChange={(e) => setHakedisForm({...hakedisForm, hakedis_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Başlık *</Label>
                <Input value={hakedisForm.title} onChange={(e) => setHakedisForm({...hakedisForm, title: e.target.value})} placeholder="Hakediş başlığı" />
              </div>
              <div className="space-y-2">
                <Label>Miktar (TL)</Label>
                <Input type="number" value={hakedisForm.amount} onChange={(e) => setHakedisForm({...hakedisForm, amount: e.target.value})} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Yapan Kişi</Label>
                <Input value={user?.name || 'Bilinmiyor'} disabled className="bg-gray-50" />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea value={hakedisForm.description} onChange={(e) => setHakedisForm({...hakedisForm, description: e.target.value})} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Belgeler</Label>
                <Input type="file" multiple onChange={(e) => handleFileUpload(e, setHakedisForm, 'documents')} />
                {hakedisForm.documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {hakedisForm.documents.map((doc, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                        <span>{doc.name}</span>
                        <Button size="sm" variant="ghost" onClick={() => removeDocument(setHakedisForm, 'documents', i)}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHakedisModalOpen(false)}>İptal</Button>
              <Button onClick={handleCreateHakedis} className="bg-amber-600 hover:bg-amber-700">Ekle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Sözleşme Modal */}
        <Dialog open={sozlesmeModalOpen} onOpenChange={setSozlesmeModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-amber-600" />Sözleşme Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Sözleşme Tarihi</Label>
                <Input type="date" value={sozlesmeForm.contract_date} onChange={(e) => setSozlesmeForm({...sozlesmeForm, contract_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>İş Türü *</Label>
                <Input value={sozlesmeForm.work_type} onChange={(e) => setSozlesmeForm({...sozlesmeForm, work_type: e.target.value})} placeholder="İş türü" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Başlangıç</Label>
                  <Input type="date" value={sozlesmeForm.start_date} onChange={(e) => setSozlesmeForm({...sozlesmeForm, start_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Bitiş</Label>
                  <Input type="date" value={sozlesmeForm.end_date} onChange={(e) => setSozlesmeForm({...sozlesmeForm, end_date: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Belgeler</Label>
                <Input type="file" multiple onChange={(e) => handleFileUpload(e, setSozlesmeForm, 'documents')} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSozlesmeModalOpen(false)}>İptal</Button>
              <Button onClick={handleCreateSozlesme} className="bg-amber-600 hover:bg-amber-700">Ekle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* İş Programı Modal */}
        <Dialog open={isProgramiModalOpen} onOpenChange={setIsProgramiModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-amber-600" />İş Programı Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Konu *</Label>
                <Input value={isProgramiForm.subject} onChange={(e) => setIsProgramiForm({...isProgramiForm, subject: e.target.value})} placeholder="Konu" />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea value={isProgramiForm.description} onChange={(e) => setIsProgramiForm({...isProgramiForm, description: e.target.value})} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Başlangıç</Label>
                  <Input type="date" value={isProgramiForm.start_date} onChange={(e) => setIsProgramiForm({...isProgramiForm, start_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Bitiş</Label>
                  <Input type="date" value={isProgramiForm.end_date} onChange={(e) => setIsProgramiForm({...isProgramiForm, end_date: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Belgeler</Label>
                <Input type="file" multiple onChange={(e) => handleFileUpload(e, setIsProgramiForm, 'documents')} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsProgramiModalOpen(false)}>İptal</Button>
              <Button onClick={handleCreateIsProgrami} className="bg-amber-600 hover:bg-amber-700">Ekle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tutanak Modal */}
        <Dialog open={tutanakModalOpen} onOpenChange={setTutanakModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-amber-600" />Tutanak Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input type="date" value={tutanakForm.report_date} onChange={(e) => setTutanakForm({...tutanakForm, report_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Başlık *</Label>
                <Input value={tutanakForm.title} onChange={(e) => setTutanakForm({...tutanakForm, title: e.target.value})} placeholder="Tutanak başlığı" />
              </div>
              <div className="space-y-2">
                <Label>İçerik</Label>
                <Textarea value={tutanakForm.content} onChange={(e) => setTutanakForm({...tutanakForm, content: e.target.value})} rows={4} />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea value={tutanakForm.description} onChange={(e) => setTutanakForm({...tutanakForm, description: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Belgeler</Label>
                <Input type="file" multiple onChange={(e) => handleFileUpload(e, setTutanakForm, 'documents')} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTutanakModalOpen(false)}>İptal</Button>
              <Button onClick={handleCreateTutanak} className="bg-amber-600 hover:bg-amber-700">Ekle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* İş Bilgisi Modal */}
        <Dialog open={isBilgisiModalOpen} onOpenChange={setIsBilgisiModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-amber-600" />İş Bilgisi Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input type="date" value={isBilgisiForm.report_date} onChange={(e) => setIsBilgisiForm({...isBilgisiForm, report_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Başlık *</Label>
                <Input value={isBilgisiForm.title} onChange={(e) => setIsBilgisiForm({...isBilgisiForm, title: e.target.value})} placeholder="İş bilgisi başlığı" />
              </div>
              <div className="space-y-2">
                <Label>İçerik</Label>
                <Textarea value={isBilgisiForm.content} onChange={(e) => setIsBilgisiForm({...isBilgisiForm, content: e.target.value})} rows={4} />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea value={isBilgisiForm.description} onChange={(e) => setIsBilgisiForm({...isBilgisiForm, description: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Belgeler</Label>
                <Input type="file" multiple onChange={(e) => handleFileUpload(e, setIsBilgisiForm, 'documents')} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsBilgisiModalOpen(false)}>İptal</Button>
              <Button onClick={handleCreateIsBilgisi} className="bg-amber-600 hover:bg-amber-700">Ekle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Rapor Modal */}
        <Dialog open={raporModalOpen} onOpenChange={setRaporModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileBarChart className="h-5 w-5 text-amber-600" />Rapor Ekle</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input type="date" value={raporForm.report_date} onChange={(e) => setRaporForm({...raporForm, report_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Rapor Türü</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={raporForm.report_type} 
                  onChange={(e) => setRaporForm({...raporForm, report_type: e.target.value})}
                >
                  <option value="daily">Günlük</option>
                  <option value="weekly">Haftalık</option>
                  <option value="monthly">Aylık</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Başlık *</Label>
                <Input value={raporForm.title} onChange={(e) => setRaporForm({...raporForm, title: e.target.value})} placeholder="Rapor başlığı" />
              </div>
              <div className="space-y-2">
                <Label>İçerik</Label>
                <Textarea value={raporForm.content} onChange={(e) => setRaporForm({...raporForm, content: e.target.value})} rows={4} />
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea value={raporForm.description} onChange={(e) => setRaporForm({...raporForm, description: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Belgeler</Label>
                <Input type="file" multiple onChange={(e) => handleFileUpload(e, setRaporForm, 'documents')} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRaporModalOpen(false)}>İptal</Button>
              <Button onClick={handleCreateRapor} className="bg-amber-600 hover:bg-amber-700">Ekle</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detay</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4 py-4">
                {selectedItem.title && <div><Label className="text-gray-500">Başlık</Label><p className="font-medium">{selectedItem.title}</p></div>}
                {selectedItem.content && <div><Label className="text-gray-500">İçerik</Label><p className="whitespace-pre-wrap">{selectedItem.content}</p></div>}
                {selectedItem.description && <div><Label className="text-gray-500">Açıklama</Label><p className="whitespace-pre-wrap">{selectedItem.description}</p></div>}
                {selectedItem.amount && <div><Label className="text-gray-500">Miktar</Label><p className="font-medium">{Number(selectedItem.amount).toLocaleString('tr-TR')} TL</p></div>}
                {selectedItem.created_by_name && <div><Label className="text-gray-500">Oluşturan</Label><p>{selectedItem.created_by_name}</p></div>}
                
                {selectedItem.documents?.length > 0 && (
                  <div>
                    <Label className="text-gray-500">Belgeler</Label>
                    <div className="mt-2 space-y-2">
                      {selectedItem.documents.map((doc, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <span>{doc.name}</span>
                          <Button size="sm" variant="outline" onClick={() => downloadDocument(doc)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6" data-testid="work-subcontractors">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="h-8 w-8 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Taşeronlar</h1>
            <p className="text-gray-500">Taşeron firmalarını görüntüleyin ve kayıt ekleyin</p>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Taşeron ara..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="pl-9 w-64" 
          />
        </div>
      </div>

      {filteredSubcontractors.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Building2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Taşeron bulunamadı</h3>
            <p className="text-gray-500">Bu şubede kayıtlı taşeron bulunmuyor</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSubcontractors.map(sub => (
            <Card key={sub.id} className="hover:shadow-lg transition-shadow cursor-pointer relative" onClick={() => openDetailView(sub)}>
              {/* Yeni kayıt badge'i */}
              {getSubcontractorNewCount(sub.id) > 0 && (
                <Badge className="absolute -top-2 -right-2 h-6 min-w-[24px] px-1 flex items-center justify-center text-xs bg-red-500 text-white z-10 animate-pulse">
                  {getSubcontractorNewCount(sub.id)} yeni
                </Badge>
              )}
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-amber-100 p-2 rounded-lg">
                      <Building2 className="h-6 w-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{sub.company_name}</h3>
                      <Badge variant="outline" className="mt-1">{sub.contacts?.length || 0} Yetkili</Badge>
                    </div>
                  </div>
                </div>
                {sub.company_duties && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{sub.company_duties}</p>
                )}
                {sub.contacts?.length > 0 && (
                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span>{sub.contacts[0].name}</span>
                      {sub.contacts[0].title && <span className="text-gray-400">- {sub.contacts[0].title}</span>}
                    </div>
                    {sub.contacts[0].phone && (
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <Phone className="h-3 w-3" /><span>{sub.contacts[0].phone}</span>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-end mt-4 pt-3 border-t">
                  <Button variant="outline" size="sm" className="gap-1">
                    <Eye className="h-4 w-4" />Detay
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkSubcontractors;
